import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Pagination,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Payment as PaymentIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  AccountBalance,
  CreditCard,
  PhoneAndroid,
  LocalAtm,
  AccountBalanceWallet,
  Schedule,
  CardGiftcard,
  MoreHoriz,
  ExpandMore,
  PersonAdd,
  Cake
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerChits, createCustomerChit, makeChitPayment, closeChitEarly, getChitHistory, getChitPayments } from '../redux/actions/chitActions';
import { getCustomers, createCustomer } from '../redux/actions/customerActions';
import { getChitGroups } from '../redux/actions/chitActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';

const CustomerChits = () => {
  const dispatch = useDispatch();
  const { customerChits, customerChitsLoading, customerChitsError, customerChitsPagination, chitHistory, chitHistoryLoading, chitPayments, chitPaymentsLoading } = useSelector(state => state.chits);
  const { customers } = useSelector(state => state.customers);
  const { chitGroups } = useSelector(state => state.chits);
  const { activePaymentModes } = useSelector(state => state.paymentModes);
  const { loading, error, success } = useSelector(state => state.chits);

  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [selectedChit, setSelectedChit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    customer_id: '',
    chit_group_id: '',
    chit_number: '',
    start_date: new Date().toISOString().split('T')[0]
  });
  const [paymentData, setPaymentData] = useState({
    payment_month: 1,
    payment_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    notes: ''
  });
  const [paymentModes, setPaymentModes] = useState([]);
  const [closeData, setCloseData] = useState({
    closure_type: 'early',
    closure_amount: '',
    notes: ''
  });
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: '',
    date_of_birth: ''
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [makeInitialPayment, setMakeInitialPayment] = useState(false);
  const [initialPaymentData, setInitialPaymentData] = useState({
    payment_month: 1,
    payment_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    notes: ''
  });
  const [initialPaymentModes, setInitialPaymentModes] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  // Payment mode icons mapping
  const paymentIcons = {
    'CASH': <LocalAtm />,
    'CARD': <CreditCard />,
    'UPI': <PhoneAndroid />,
    'BANK_TRANSFER': <AccountBalance />,
    'WALLET': <AccountBalanceWallet />,
    'CHEQUE': <Schedule />,
    'GIFT_CARD': <CardGiftcard />,
    'OTHER': <MoreHoriz />,
  };

  useEffect(() => {
    fetchData();
  }, [page, searchTerm, statusFilter]);

  const fetchData = () => {
    dispatch(getCustomerChits({
      page,
      limit: 10,
      search: searchTerm,
      status: statusFilter
    }));
    dispatch(getCustomers({ page: 1, limit: 1000 }));
    dispatch(getChitGroups({ page: 1, limit: 1000 }));
    dispatch(getActivePaymentModes());
  };


  const handleOpenDialog = () => {
    setFormData({
      customer_id: '',
      chit_group_id: '',
      chit_number: '',
      start_date: new Date().toISOString().split('T')[0]
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      customer_id: '',
      chit_group_id: '',
      chit_number: '',
      start_date: new Date().toISOString().split('T')[0]
    });
  };

  const handleOpenPaymentDialog = (chit) => {
    setSelectedChit(chit);
    
    // Calculate next available month (assuming 12 months total)
    const nextMonth = Math.min(12, Math.floor(chit.paid_amount / chit.monthly_installment) + 1);
    
    setPaymentData({
      payment_month: nextMonth,
      payment_date: new Date().toISOString().split('T')[0],
      total_amount: chit.monthly_installment,
      notes: ''
    });
    setPaymentModes([]);
    setPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialog(false);
    setSelectedChit(null);
  };

  const handleOpenCloseDialog = (chit) => {
    setSelectedChit(chit);
    setCloseData({
      closure_type: 'early',
      closure_amount: chit.paid_amount > 0 ? chit.paid_amount : chit.remaining_amount,
      notes: ''
    });
    setCloseDialog(true);
  };

  const handleCloseCloseDialog = () => {
    setCloseDialog(false);
    setSelectedChit(null);
  };

  const handleOpenHistoryDialog = (chit) => {
    setSelectedChit(chit);
    dispatch(getChitHistory(chit.id));
    dispatch(getChitPayments({ customer_chit_id: chit.id }));
    setHistoryDialog(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialog(false);
    setSelectedChit(null);
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const result = await dispatch(createCustomer(newCustomer));
      if (result.success) {
        setOpenCustomerDialog(false);
        setNewCustomer({
          name: '',
          phone: '',
          email: '',
          address: '',
          gstin: '',
          date_of_birth: ''
        });
        // Refresh customers list
        dispatch(getCustomers({ page: 1, limit: 1000 }));
        // Set the newly created customer as selected
        setSelectedCustomer(result.data);
        setFormData(prev => ({ ...prev, customer_id: result.data.id }));
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  // Initial payment handlers
  const handleInitialPaymentModeToggle = (mode) => {
    const existingIndex = initialPaymentModes.findIndex(p => p.modeId === mode.id);
    if (existingIndex >= 0) {
      setInitialPaymentModes(prev => prev.filter((_, index) => index !== existingIndex));
    } else {
      setInitialPaymentModes(prev => [...prev, {
        modeId: mode.id,
        modeName: mode.name,
        modeCode: mode.code,
        amount: '',
        reference: '',
        bankDetails: '',
        requiresReference: mode.requires_reference,
        requiresBank: mode.requires_bank
      }]);
    }
  };

  const handleInitialPaymentAmountChange = (modeId, value) => {
    setInitialPaymentModes(prev => prev.map(p => 
      p.modeId === modeId ? { ...p, amount: value } : p
    ));
  };

  const handleInitialPaymentReferenceChange = (modeId, value) => {
    setInitialPaymentModes(prev => prev.map(p => 
      p.modeId === modeId ? { ...p, reference: value } : p
    ));
  };

  const handleInitialPaymentBankDetailsChange = (modeId, value) => {
    setInitialPaymentModes(prev => prev.map(p => 
      p.modeId === modeId ? { ...p, bankDetails: value } : p
    ));
  };

  const getInitialPaymentTotal = () => {
    return initialPaymentModes.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  };

  const getInitialPaymentRemaining = () => {
    const total = parseFloat(initialPaymentData.total_amount || 0);
    const paid = getInitialPaymentTotal();
    return total - paid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloseInputChange = (e) => {
    const { name, value } = e.target;
    setCloseData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Payment mode handling functions
  const handlePaymentModeToggle = (mode) => {
    setPaymentModes(prev => {
      const existing = prev.find(p => p.modeId === mode.id);
      if (existing) {
        return prev.filter(p => p.modeId !== mode.id);
      } else {
        return [...prev, {
          modeId: mode.id,
          modeCode: mode.code,
          modeName: mode.name,
          amount: '',
          reference: '',
          bankDetails: '',
          requiresReference: mode.requires_reference,
          requiresBank: mode.requires_bank,
        }];
      }
    });
  };

  const handlePaymentAmountChange = (modeId, amount) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, amount } : p)
    );
  };

  const handlePaymentReferenceChange = (modeId, reference) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, reference } : p)
    );
  };

  const handlePaymentBankDetailsChange = (modeId, bankDetails) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, bankDetails } : p)
    );
  };

  const calculatePaymentTotal = () => {
    return paymentModes.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  };

  const getRemainingAmount = () => {
    const totalAmount = parseFloat(paymentData.total_amount) || 0;
    const paymentTotal = calculatePaymentTotal();
    return totalAmount - paymentTotal;
  };

  const getPaymentIcon = (iconName) => {
    return paymentIcons[iconName] || <PaymentIcon />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      
      // Create the chit first
      const result = await dispatch(createCustomerChit(formData));
      if (result.success) {
        const chitId = result.data.id;
        
        // If initial payment is enabled and payment modes are selected
        if (makeInitialPayment && initialPaymentModes.length > 0) {
          // Prepare payment data
          const paymentData = {
            customer_chit_id: chitId,
            payment_month: initialPaymentData.payment_month,
            payment_date: initialPaymentData.payment_date,
            total_amount: initialPaymentData.total_amount,
            notes: initialPaymentData.notes,
            payment_modes: initialPaymentModes
          };
          
          // Make the initial payment
          await dispatch(makeChitPayment(paymentData));
        }
        
        // Reset form and close dialog
        handleCloseDialog();
        setFormData({
          customer_id: '',
          chit_group_id: '',
          chit_number: '',
          start_date: ''
        });
        setSelectedCustomer(null);
        setMakeInitialPayment(false);
        setInitialPaymentModes([]);
        setInitialPaymentData({
          payment_month: 1,
          payment_date: new Date().toISOString().split('T')[0],
          total_amount: '',
          notes: ''
        });
        
        // Refresh the chits list
        fetchData();
      }
    } catch (error) {
      console.error('Error creating customer chit:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (paymentModes.length === 0) {
      alert('Please select at least one payment mode');
      return;
    }

    const remainingAmount = getRemainingAmount();
    if (Math.abs(remainingAmount) > 0.01) {
      alert(`Payment total (₹${calculatePaymentTotal()}) does not match invoice total (₹${paymentData.total_amount}). Remaining: ₹${remainingAmount}`);
      return;
    }

    try {
      const result = await dispatch(makeChitPayment({
        customer_chit_id: selectedChit.id,
        ...paymentData,
        payment_modes: paymentModes.map(p => ({
          payment_mode_id: p.modeId,
          amount: parseFloat(p.amount),
          reference_number: p.reference,
          bank_details: p.bankDetails
        }))
      }));
      
      if (result.success) {
        handleClosePaymentDialog();
        fetchData();
      } else {
        alert(result.message || 'Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error making payment:', error);
      alert('Payment failed. Please try again.');
    }
  };

  const handleCloseSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(closeChitEarly(selectedChit.id, closeData));
      handleCloseCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error closing chit:', error);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setStatusFilter(newValue === 0 ? 'active' : newValue === 1 ? 'completed' : 'closed_early');
    setPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'primary';
      case 'closed_early': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Customer Chits
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add Customer Chit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" gap={2} mb={3}>
            <TextField
              placeholder="Search customer chits..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 300 }}
            />
          </Box>

          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Active" />
            <Tab label="Completed" />
            <Tab label="Closed Early" />
          </Tabs>

          {customerChitsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Chit Group</TableCell>
                      <TableCell>Chit Number</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Paid Amount</TableCell>
                      <TableCell>Remaining</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerChits.map((chit) => (
                      <TableRow key={chit.id}>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {chit.customer?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {chit.customer?.phone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {chit.chitGroup?.group_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(chit.chitGroup?.chit_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>{chit.chit_number}</TableCell>
                        <TableCell>{new Date(chit.start_date).toLocaleDateString()}</TableCell>
                        <TableCell>{formatCurrency(chit.total_amount)}</TableCell>
                        <TableCell>{formatCurrency(chit.paid_amount)}</TableCell>
                        <TableCell>{formatCurrency(chit.remaining_amount)}</TableCell>
                        <TableCell>
                          <Chip
                            label={chit.status.replace('_', ' ').toUpperCase()}
                            color={getStatusColor(chit.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {chit.status === 'active' && (
                            <>
                              <Tooltip title="Make Payment">
                                <IconButton size="small" onClick={() => handleOpenPaymentDialog(chit)}>
                                  <PaymentIcon />
                                </IconButton>
                              </Tooltip>
                              {chit.paid_amount > 0 && (
                                <Tooltip title="Close Early">
                                  <IconButton size="small" onClick={() => handleOpenCloseDialog(chit)}>
                                    <CloseIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                          <Tooltip title="View History">
                            <IconButton size="small" onClick={() => handleOpenHistoryDialog(chit)}>
                              <HistoryIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {customerChitsPagination && customerChitsPagination.pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={customerChitsPagination.pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Customer Chit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                Add Customer Chit
              </Typography>
              {selectedCustomer && (
                <Typography variant="body2" color="text.secondary">
                  Customer: {selectedCustomer.name} - {selectedCustomer.phone}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonAdd color="primary" />
                  Customer Selection
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.name}
                    value={selectedCustomer}
                    onChange={(event, newValue) => {
                      setSelectedCustomer(newValue);
                      setFormData(prev => ({ ...prev, customer_id: newValue?.id || '' }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Customer"
                        placeholder="Search or select customer"
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" key={option.id} {...props}>
                        <Box>
                          <Typography variant="body1">{option.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.phone} {option.gstin && `• ${option.gstin}`}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCustomerDialog(true)}
                    sx={{ minWidth: 'auto' }}
                  >
                    Add New
                  </Button>
                </Box>
                {selectedCustomer && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Phone:</strong> {selectedCustomer.phone || 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong> {selectedCustomer.email || 'N/A'}
                    </Typography>
                    {selectedCustomer.address && (
                      <Typography variant="body2">
                        <strong>Address:</strong> {selectedCustomer.address}
                      </Typography>
                    )}
                    {selectedCustomer.gstin && (
                      <Typography variant="body2">
                        <strong>GSTIN:</strong> {selectedCustomer.gstin}
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Chit Group</InputLabel>
                  <Select
                    name="chit_group_id"
                    value={formData.chit_group_id}
                    onChange={handleInputChange}
                    label="Chit Group"
                    disabled={!chitGroups || chitGroups.length === 0}
                  >
                    {chitGroups && chitGroups.length > 0 ? (
                      chitGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.group_name} - {formatCurrency(group.chit_amount)}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        {chitGroups === undefined ? 'Loading...' : 'No chit groups available'}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Chit Number"
                  name="chit_number"
                  value={formData.chit_number}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              {/* Initial Payment Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon color="primary" />
                    Initial Payment
                  </Typography>
                  <Checkbox
                    checked={makeInitialPayment}
                    onChange={(e) => setMakeInitialPayment(e.target.checked)}
                    color="primary"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Make initial payment for this chit
                  </Typography>
                </Box>
              </Grid>
              
              {makeInitialPayment && (
                <>
                  {/* Payment Details */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Payment Month"
                      type="number"
                      value={initialPaymentData.payment_month}
                      onChange={(e) => setInitialPaymentData(prev => ({ ...prev, payment_month: parseInt(e.target.value) }))}
                      inputProps={{ min: 1, max: 12 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Payment Date"
                      type="date"
                      value={initialPaymentData.payment_date}
                      onChange={(e) => setInitialPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Payment Amount"
                      type="number"
                      value={initialPaymentData.total_amount}
                      onChange={(e) => setInitialPaymentData(prev => ({ ...prev, total_amount: e.target.value }))}
                      placeholder="Enter payment amount"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Notes (Optional)"
                      value={initialPaymentData.notes}
                      onChange={(e) => setInitialPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Payment notes"
                    />
                  </Grid>
                  
                  {/* Payment Modes Selection */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CreditCard color="primary" />
                      Payment Modes
                    </Typography>
                    
                    {/* Payment Modes Accordion */}
                    <Box sx={{ mb: 2 }}>
                      {activePaymentModes.map((mode) => {
                        const isSelected = initialPaymentModes.some(p => p.modeId === mode.id);
                        const selectedPayment = initialPaymentModes.find(p => p.modeId === mode.id);
                        
                        return (
                          <Accordion 
                            key={mode.id} 
                            expanded={isSelected}
                            onChange={() => handleInitialPaymentModeToggle(mode)}
                            sx={{ mb: 1 }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore />}
                              sx={{ 
                                backgroundColor: isSelected ? 'primary.light' : 'grey.50',
                                '&:hover': { backgroundColor: isSelected ? 'primary.light' : 'grey.100' }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleInitialPaymentModeToggle(mode)}
                                  onClick={(e) => e.stopPropagation()}
                                  color="primary"
                                />
                                <Box sx={{ color: mode.color || 'primary.main', display: 'flex', alignItems: 'center' }}>
                                  {getPaymentIcon(mode.icon_name)}
                                </Box>
                                <Typography variant="body1" fontWeight={isSelected ? 'bold' : 'normal'}>
                                  {mode.name}
                                </Typography>
                                {isSelected && selectedPayment?.amount && (
                                  <Chip 
                                    label={`₹${selectedPayment.amount}`} 
                                    size="small" 
                                    color="primary" 
                                    sx={{ ml: 'auto' }}
                                  />
                                )}
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                  fullWidth
                                  label="Amount (₹)"
                                  type="number"
                                  value={selectedPayment?.amount || ''}
                                  onChange={(e) => handleInitialPaymentAmountChange(mode.id, e.target.value)}
                                  placeholder="Enter amount"
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                  }}
                                  inputProps={{ step: "0.01", min: 0 }}
                                />
                                
                                {mode.requires_reference && (
                                  <TextField
                                    fullWidth
                                    label="Transaction Reference"
                                    value={selectedPayment?.reference || ''}
                                    onChange={(e) => handleInitialPaymentReferenceChange(mode.id, e.target.value)}
                                    placeholder="Enter transaction reference number"
                                  />
                                )}
                                
                                {mode.requires_bank && (
                                  <TextField
                                    fullWidth
                                    label="Bank Details"
                                    value={selectedPayment?.bankDetails || ''}
                                    onChange={(e) => handleInitialPaymentBankDetailsChange(mode.id, e.target.value)}
                                    multiline
                                    rows={2}
                                    placeholder="Enter bank name, account number, etc."
                                  />
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                    
                    {/* Payment Summary */}
                    {initialPaymentModes.length > 0 && (
                      <Card sx={{ 
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white'
                      }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalAtm />
                            Payment Summary
                          </Typography>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1" sx={{ color: 'white' }}>
                              Total Amount:
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                              ₹{parseFloat(initialPaymentData.total_amount || 0).toLocaleString()}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1" sx={{ color: 'white' }}>
                              Payment Total:
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                              ₹{getInitialPaymentTotal().toLocaleString()}
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ color: 'white' }}>
                              {getInitialPaymentRemaining() === 0 ? '✅ Payment Balanced' : '⚠️ Amount Mismatch'}
                            </Typography>
                            <Typography 
                              variant="h5" 
                              sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                backgroundColor: getInitialPaymentRemaining() === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.3)',
                                px: 2,
                                py: 1,
                                borderRadius: 1
                              }}
                            >
                              ₹{Math.abs(getInitialPaymentRemaining())}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={formLoading || (makeInitialPayment && initialPaymentModes.length === 0) || (makeInitialPayment && Math.abs(getInitialPaymentRemaining()) > 0.01)}
            >
              {formLoading ? <CircularProgress size={20} /> : (makeInitialPayment ? 'Create Chit & Make Payment' : 'Create Chit')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onClose={handleClosePaymentDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                Make Payment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customer: {selectedChit?.customer?.name} - {selectedChit?.customer?.phone}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Chit: {selectedChit?.chit_number} - {selectedChit?.chitGroup?.group_name}
              </Typography>
            </Box>
            <Chip 
              label={`Month ${paymentData.payment_month}`} 
              color="primary" 
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        </DialogTitle>
        <form onSubmit={handlePaymentSubmit}>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              {/* Chit Summary Card */}
              {selectedChit && (
                <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                      Chit Payment Summary
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Total Amount
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {formatCurrency(selectedChit.total_amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Paid Amount
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {formatCurrency(selectedChit.paid_amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Remaining
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {formatCurrency(selectedChit.remaining_amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Monthly Installment
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {formatCurrency(selectedChit.monthly_installment)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Payment Details Form */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon color="primary" />
                    Payment Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Payment Month"
                        name="payment_month"
                        type="number"
                        value={paymentData.payment_month}
                        onChange={handlePaymentInputChange}
                        required
                        inputProps={{ min: 1, max: 12 }}
                        helperText={selectedChit ? `Next available month: ${Math.min(12, Math.floor(selectedChit.paid_amount / selectedChit.monthly_installment) + 1)}` : ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Payment Date"
                        name="payment_date"
                        type="date"
                        value={paymentData.payment_date}
                        onChange={handlePaymentInputChange}
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Total Amount"
                        name="total_amount"
                        type="number"
                        value={paymentData.total_amount}
                        onChange={handlePaymentInputChange}
                        required
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>₹</Typography>
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Notes (Optional)"
                        name="notes"
                        multiline
                        rows={2}
                        value={paymentData.notes}
                        onChange={handlePaymentInputChange}
                        placeholder="Add any additional notes..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Payment Modes Selection - Following Billing Page Pattern */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCard color="primary" />
                    Payment Modes
                  </Typography>
                  
                  {/* Payment Modes Accordion */}
                  <Box sx={{ mb: 2 }}>
                    {activePaymentModes.map((mode) => {
                      const isSelected = paymentModes.some(p => p.modeId === mode.id);
                      const selectedPayment = paymentModes.find(p => p.modeId === mode.id);
                      
                      return (
                        <Accordion 
                          key={mode.id} 
                          expanded={isSelected}
                          onChange={() => handlePaymentModeToggle(mode)}
                          sx={{ mb: 1 }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{ 
                              backgroundColor: isSelected ? 'primary.light' : 'grey.50',
                              '&:hover': { backgroundColor: isSelected ? 'primary.light' : 'grey.100' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handlePaymentModeToggle(mode)}
                                onClick={(e) => e.stopPropagation()}
                                color="primary"
                              />
                              <Box sx={{ color: mode.color || 'primary.main', display: 'flex', alignItems: 'center' }}>
                                {getPaymentIcon(mode.icon_name)}
                              </Box>
                              <Typography variant="body1" fontWeight={isSelected ? 'bold' : 'normal'}>
                                {mode.name}
                              </Typography>
                              {isSelected && selectedPayment?.amount && (
                                <Chip 
                                  label={`₹${selectedPayment.amount}`} 
                                  size="small" 
                                  color="primary" 
                                  sx={{ ml: 'auto' }}
                                />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <TextField
                                fullWidth
                                label="Amount (₹)"
                                type="number"
                                value={selectedPayment?.amount || ''}
                                onChange={(e) => handlePaymentAmountChange(mode.id, e.target.value)}
                                placeholder="Enter amount"
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                }}
                                inputProps={{ step: "0.01", min: 0 }}
                              />
                              
                              {mode.requires_reference && (
                                <TextField
                                  fullWidth
                                  label="Transaction Reference"
                                  value={selectedPayment?.reference || ''}
                                  onChange={(e) => handlePaymentReferenceChange(mode.id, e.target.value)}
                                  placeholder="Enter transaction reference number"
                                />
                              )}
                              
                              {mode.requires_bank && (
                                <TextField
                                  fullWidth
                                  label="Bank Details"
                                  value={selectedPayment?.bankDetails || ''}
                                  onChange={(e) => handlePaymentBankDetailsChange(mode.id, e.target.value)}
                                  multiline
                                  rows={2}
                                  placeholder="Enter bank name, account number, etc."
                                />
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>

              {/* Payment Summary */}
              {paymentModes.length > 0 && (
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white'
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalAtm />
                      Payment Summary
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Total Amount
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                          ₹{paymentData.total_amount}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Payment Total
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                          ₹{calculatePaymentTotal()}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'white' }}>
                        {getRemainingAmount() === 0 ? '✅ Payment Balanced' : '⚠️ Amount Mismatch'}
                      </Typography>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: 'white', 
                          fontWeight: 'bold',
                          backgroundColor: getRemainingAmount() === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.3)',
                          px: 2,
                          py: 1,
                          borderRadius: 1
                        }}
                      >
                        ₹{Math.abs(getRemainingAmount())}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Button 
              onClick={handleClosePaymentDialog}
              variant="outlined"
              size="large"
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              size="large"
              disabled={loading || paymentModes.length === 0 || Math.abs(getRemainingAmount()) > 0.01}
              sx={{ 
                minWidth: 160,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Record Payment'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Close Chit Dialog */}
      <Dialog open={closeDialog} onClose={handleCloseCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              Close Chit Early
            </Typography>
            {selectedChit && (
              <Typography variant="body2" color="text.secondary">
                Customer: {selectedChit.customer?.name} - {selectedChit.customer?.phone}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <form onSubmit={handleCloseSubmit}>
          <DialogContent>
            {/* Chit Summary */}
            {selectedChit && (
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Chit Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Chit Number</Typography>
                      <Typography variant="subtitle1" fontWeight="bold">{selectedChit.chit_number}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                      <Typography variant="subtitle1" fontWeight="bold">{formatCurrency(selectedChit.total_amount)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                      <Typography variant="subtitle1" color="success.main" fontWeight="bold">{formatCurrency(selectedChit.paid_amount)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Remaining</Typography>
                      <Typography variant="subtitle1" color="warning.main" fontWeight="bold">{formatCurrency(selectedChit.remaining_amount)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Closure Type</InputLabel>
                  <Select
                    name="closure_type"
                    value={closeData.closure_type}
                    onChange={handleCloseInputChange}
                    label="Closure Type"
                  >
                    <MenuItem value="early">Early Closure</MenuItem>
                    <MenuItem value="cash_withdrawal">Cash Withdrawal</MenuItem>
                    <MenuItem value="gold_purchase">Gold Purchase</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Closure Amount"
                  name="closure_amount"
                  type="number"
                  value={closeData.closure_amount}
                  onChange={handleCloseInputChange}
                  required
                  helperText={selectedChit ? `Customer has paid: ₹${selectedChit.paid_amount || 0}` : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  multiline
                  rows={3}
                  value={closeData.notes}
                  onChange={handleCloseInputChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Close Chit'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog} onClose={handleCloseHistoryDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Chit History - {selectedChit?.chit_number}</Typography>
              <Typography variant="body2" color="text.secondary">
                Customer: {selectedChit?.customer?.name} - {selectedChit?.customer?.phone}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedChit?.chitGroup?.group_name} - {formatCurrency(selectedChit?.chitGroup?.chit_amount)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {chitHistoryLoading || chitPaymentsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Chit Summary */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Chit Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6">{formatCurrency(selectedChit?.total_amount)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                      <Typography variant="h6" color="success.main">{formatCurrency(selectedChit?.paid_amount)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Remaining</Typography>
                      <Typography variant="h6" color="warning.main">{formatCurrency(selectedChit?.remaining_amount)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip 
                        label={selectedChit?.status?.replace('_', ' ').toUpperCase()} 
                        color={selectedChit?.status === 'active' ? 'success' : 'primary'}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Payment History */}
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Payment History ({chitPayments?.length || 0} payments)
              </Typography>
              
              {chitPayments && chitPayments.length > 0 ? (
                <Box>
                  {chitPayments.map((payment, index) => (
                    <Card key={payment.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="h6">
                            Month {payment.payment_month}
                          </Typography>
                          <Box textAlign="right">
                            <Typography variant="h6" color="primary">
                              {formatCurrency(payment.amount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(payment.payment_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Payment Mode Details */}
                        {payment.paymentDetails && payment.paymentDetails.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Payment Breakdown:
                            </Typography>
                            <Grid container spacing={1}>
                              {payment.paymentDetails.map((detail, detailIndex) => (
                                <Grid item xs={12} sm={6} key={detailIndex}>
                                  <Box 
                                    sx={{ 
                                      p: 1, 
                                      border: 1, 
                                      borderColor: 'divider', 
                                      borderRadius: 1,
                                      bgcolor: 'grey.50'
                                    }}
                                  >
                                    <Box display="flex" alignItems="center" mb={0.5}>
                                      {getPaymentIcon(detail.paymentMode?.icon_name)}
                                      <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                                        {detail.paymentMode?.name}
                                      </Typography>
                                    </Box>
                                    <Typography variant="h6" color="primary">
                                      {formatCurrency(detail.amount)}
                                    </Typography>
                                    {detail.reference_number && (
                                      <Typography variant="caption" color="text.secondary">
                                        Ref: {detail.reference_number}
                                      </Typography>
                                    )}
                                    {detail.bank_details && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Bank: {detail.bank_details}
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                        
                        {payment.notes && (
                          <Box mt={1}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Notes:</strong> {payment.notes}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={3}>
                  No payments recorded yet
                </Typography>
              )}

              {/* Activity History */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                Activity History ({chitHistory?.length || 0} activities)
              </Typography>
              
              {chitHistory && chitHistory.length > 0 ? (
                <Box>
                  {chitHistory.map((activity, index) => (
                    <Card key={activity.id} sx={{ mb: 1 }}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {activity.action_type?.replace('_', ' ').toUpperCase()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              by {activity.user?.username} • {new Date(activity.action_date).toLocaleString()}
                            </Typography>
                          </Box>
                          {activity.amount && (
                            <Typography variant="h6" color="primary">
                              {formatCurrency(activity.amount)}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={3}>
                  No activity history available
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add New Customer Dialog */}
      <Dialog 
        open={openCustomerDialog} 
        onClose={() => setOpenCustomerDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <Box sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 0
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 0
          }} />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Add New Customer
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Create a new customer profile for chit management
            </Typography>
          </Box>
        </Box>
        
        <DialogContent sx={{ p: 4, bgcolor: '#fafafa' }}>
          <form onSubmit={handleCreateCustomer}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  required
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: { fontWeight: 600 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  required
                  variant="outlined"
                  placeholder="+91 9876543210"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: { fontWeight: 600 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  variant="outlined"
                  placeholder="customer@example.com"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: { fontWeight: 600 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={newCustomer.date_of_birth || ''}
                  onChange={(e) => setNewCustomer({ ...newCustomer, date_of_birth: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 600 }
                  }}
                  helperText="Used for sending birthday wishes"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Cake color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={3}
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  variant="outlined"
                  placeholder="Enter complete address with city, state, and pincode"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: { fontWeight: 600 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="GSTIN (Optional)"
                  value={newCustomer.gstin}
                  onChange={(e) => setNewCustomer({ ...newCustomer, gstin: e.target.value })}
                  variant="outlined"
                  placeholder="22AAAAA0000A1Z5"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: { fontWeight: 600 }
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: '#fafafa', gap: 2 }}>
          <Button 
            onClick={() => setOpenCustomerDialog(false)}
            variant="outlined"
            size="large"
            sx={{ minWidth: 120 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCustomer}
            variant="contained" 
            size="large"
            disabled={!newCustomer.name || !newCustomer.name.trim() || !newCustomer.phone || !newCustomer.phone.trim()}
            sx={{ 
              minWidth: 160,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            Create Customer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerChits;
